import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Nav, Row, Spinner, Table } from 'react-bootstrap';
import { API_BASE_URL, getTeamProfile, saveTeamProfile, resetTeamProfile, uploadImages } from '../api';
import TeamContent from './TeamContent';

// Website-owner editor for the "Our Team" page (Docs/requirements/common/homepage/our_team_page.md).
// One doc per scope: 'platform' is the homepage team; each vertical either inherits it or has its
// own. Tabs: Content / People (paginated table + add/edit modal) / Hierarchy (adjust reporting
// lines on the UI) / Preview / JSON. Edited from ANY vertical's AdminDashboard.
const SCOPES = [
  ['platform', 'Platform (homepage)'],
  ['jewelry', 'Jewellery Rental'],
  ['car_rental', 'Car Rental'],
  ['real_estate', 'Real Estate'],
  ['hospitality', 'Hospitality'],
  ['gym', 'Gym Memberships'],
];
const SENIORITY = [['exec', 'Executive (top of chart)'], ['lead', 'Function lead'], ['member', 'Team member']];
const SENIORITY_LABEL = Object.fromEntries(SENIORITY);
const PAGE_SIZE = 8;

const emptyMember = () => ({
  name: '', title: '', photoUrl: '', bio: '', seniority: 'member', reportsTo: '',
  functionKeys: [], expertise: [], order: 100,
});

function toForm(p) {
  return {
    useOwnTeam: p.useOwnTeam ?? (p.scope === 'platform'),
    orgName: p.orgName || '',
    intro: p.intro || '',
    story: p.story?.length ? [...p.story] : [''],
    principles: p.principles?.length ? p.principles.map((x) => ({ ...x })) : [],
    functions: p.functions?.length ? p.functions.map((x) => ({ ...x })) : [],
    expertiseAreas: p.expertiseAreas?.length ? [...p.expertiseAreas] : [],
    members: p.members?.length ? p.members.map((m, i) => ({ ...emptyMember(), order: (i + 1) * 10, ...m })) : [],
  };
}

function toPayload(form) {
  return {
    useOwnTeam: form.useOwnTeam,
    orgName: form.orgName.trim(),
    intro: form.intro.trim(),
    story: form.story.map((s) => s.trim()).filter(Boolean),
    principles: form.principles.filter((p) => (p.title || '').trim() || (p.body || '').trim()),
    functions: form.functions.filter((f) => (f.key || '').trim() || (f.label || '').trim())
      .map((f) => ({ key: (f.key || '').trim().toLowerCase().replace(/\s+/g, '_'), label: (f.label || '').trim(), description: (f.description || '').trim() })),
    expertiseAreas: form.expertiseAreas.map((s) => s.trim()).filter(Boolean),
    members: form.members
      .filter((m) => (m.name || '').trim() || (m.title || '').trim())
      .map((m, i) => ({
        name: (m.name || '').trim(), title: (m.title || '').trim(), photoUrl: m.photoUrl || '',
        bio: (m.bio || '').trim(), seniority: m.seniority || 'member', reportsTo: (m.reportsTo || '').trim(),
        functionKeys: (m.functionKeys || []).filter(Boolean),
        expertise: (m.expertise || []).filter(Boolean),
        order: Number.isFinite(Number(m.order)) ? Number(m.order) : (i + 1) * 10,
      })),
  };
}

function photoSrc(url) {
  if (!url) return '';
  return url.startsWith('/uploads/') ? `${API_BASE_URL}${url}` : url;
}

// ---- the whole panel ----
export default function TeamProfilePanel() {
  const [scope, setScope] = useState('platform');
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState('content');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [editIdx, setEditIdx] = useState(null); // index into form.members, or -1 for a new one

  const load = () => {
    setLoading(true); setMsg(''); setError('');
    getTeamProfile(scope)
      .then((p) => setForm(toForm(p)))
      .catch(() => setError('Could not load the team page.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [scope]);

  const isPlatform = scope === 'platform';
  const editable = isPlatform || (form && form.useOwnTeam);

  const patch = (up) => setForm((f) => ({ ...f, ...(typeof up === 'function' ? up(f) : up) }));
  const set = (field) => (e) => patch({ [field]: e.target.value });

  // list helpers (story / principles / functions)
  const setListItem = (key, idx, val) => patch((f) => ({ [key]: f[key].map((x, i) => (i === idx ? (typeof val === 'object' ? { ...x, ...val } : val) : x)) }));
  const addListItem = (key, item) => patch((f) => ({ [key]: [...f[key], item] }));
  const removeListItem = (key, idx) => patch((f) => ({ [key]: f[key].filter((_, i) => i !== idx) }));
  const moveListItem = (key, idx, dir) => patch((f) => {
    const arr = [...f[key]]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return {};
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { [key]: arr };
  });

  // member helpers
  const upsertMember = (idx, member) => patch((f) => {
    const members = idx < 0 ? [...f.members, member] : f.members.map((m, i) => (i === idx ? member : m));
    return { members };
  });
  const removeMember = (idx) => patch((f) => ({ members: f.members.filter((_, i) => i !== idx) }));
  const setMemberManager = (idx, reportsTo) => patch((f) => ({ members: f.members.map((m, i) => (i === idx ? { ...m, reportsTo } : m)) }));
  // move a member up/down among its siblings (same manager) by swapping order values
  const moveInHierarchy = (idx, dir) => patch((f) => {
    const me = f.members[idx];
    const siblings = f.members
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => (m.reportsTo || '') === (me.reportsTo || ''))
      .sort((a, b) => (a.m.order ?? 0) - (b.m.order ?? 0));
    const pos = siblings.findIndex((s) => s.i === idx);
    const swap = siblings[pos + dir];
    if (!swap) return {};
    const members = f.members.map((m, i) => {
      if (i === idx) return { ...m, order: swap.m.order ?? 0 };
      if (i === swap.i) return { ...m, order: me.order ?? 0 };
      return m;
    });
    return { members };
  });

  const save = async () => {
    setSaving(true); setMsg(''); setError('');
    try {
      const saved = await saveTeamProfile(scope, toPayload(form));
      setForm(toForm(saved));
      setMsg(isPlatform ? 'Saved. The homepage "Our Team" page now shows this.' : 'Saved.');
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to save the team page.');
    } finally { setSaving(false); }
  };

  const doReset = async () => {
    if (!window.confirm(isPlatform
      ? 'Reset the platform team page to the built-in defaults? This replaces your current content.'
      : "Drop this vertical's own team page and go back to inheriting the platform team?")) return;
    setSaving(true); setError('');
    try {
      setForm(toForm(await resetTeamProfile(scope)));
      setMsg('Reset done.');
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to reset.');
    } finally { setSaving(false); }
  };

  const openJson = () => { setJsonText(JSON.stringify(toPayload(form), null, 2)); setJsonError(''); setTab('json'); };
  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setForm(toForm({ scope, ...parsed }));
      setJsonError(''); setTab('content'); setMsg('JSON applied to the form. Review, then Save.');
    } catch (err) { setJsonError('That is not valid JSON: ' + err.message); }
  };

  return (
    <div style={{ maxWidth: 960 }}>
      <p className="text-muted small">
        The "Our Team" page shown on the homepage (and optionally on each vertical) — the story,
        the roles &amp; reporting hierarchy, and the people with photos, expertise and
        cross-functional roles.
      </p>

      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <Form.Group style={{ maxWidth: 240 }}>
          <Form.Label className="small mb-0">Team page</Form.Label>
          <Form.Select size="sm" value={scope} onChange={(e) => setScope(e.target.value)}>
            {SCOPES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </Form.Select>
        </Form.Group>
        {!isPlatform && form && (
          <Form.Check
            type="switch" id="team-useown" className="ms-1 mb-1"
            label="This business has its own team page"
            checked={form.useOwnTeam}
            onChange={(e) => patch({ useOwnTeam: e.target.checked })}
          />
        )}
      </div>

      {msg && <Alert variant="success" className="py-2" dismissible onClose={() => setMsg('')}>{msg}</Alert>}
      {error && <Alert variant="danger" className="py-2" dismissible onClose={() => setError('')}>{error}</Alert>}

      {loading || !form ? (
        <Spinner animation="border" size="sm" role="status" />
      ) : (
        <>
          <Nav variant="tabs" activeKey={tab} onSelect={(k) => (k === 'json' ? openJson() : setTab(k))} className="mb-3">
            <Nav.Item><Nav.Link eventKey="content">Content</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link eventKey="people">People ({form.members.length})</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link eventKey="hierarchy">Hierarchy</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link eventKey="preview">Preview</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link eventKey="json">Edit as JSON</Nav.Link></Nav.Item>
          </Nav>

          {!editable && tab !== 'preview' && (
            <Alert variant="light" className="small">
              This vertical currently shows the <strong>platform</strong> team. Turn on
              "This business has its own team page" above to give it a different one.
            </Alert>
          )}

          <fieldset disabled={!editable && tab !== 'preview'} style={{ opacity: editable || tab === 'preview' ? 1 : 0.55 }}>
            {tab === 'content' && (
              <ContentTab
                form={form} set={set}
                setListItem={setListItem} addListItem={addListItem} removeListItem={removeListItem} moveListItem={moveListItem}
                setExpertise={(v) => patch({ expertiseAreas: v })}
              />
            )}

            {tab === 'people' && (
              <PeopleTab
                form={form}
                onAdd={() => setEditIdx(-1)}
                onEdit={setEditIdx}
                onRemove={removeMember}
              />
            )}

            {tab === 'hierarchy' && (
              <HierarchyTab
                form={form}
                onAdd={() => setEditIdx(-1)}
                onEdit={setEditIdx}
                setManager={setMemberManager}
                move={moveInHierarchy}
              />
            )}

            {tab === 'preview' && (
              <div className="border rounded p-3 bg-white">
                <div className="small text-muted mb-2">
                  Preview — {editable ? 'this content' : 'this vertical inherits the platform team'}
                </div>
                <TeamContent data={toPayload(form)} apiBase={API_BASE_URL} />
              </div>
            )}

            {tab === 'json' && (
              <div>
                <p className="small text-muted">
                  Paste your organization's team content as JSON (same shape as the defaults), then
                  Apply. Photos can be uploaded per person afterwards on the People tab.
                </p>
                {jsonError && <Alert variant="danger" className="py-2">{jsonError}</Alert>}
                <Form.Control
                  as="textarea" rows={16} value={jsonText} onChange={(e) => setJsonText(e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
                <div className="d-flex gap-2 mt-2">
                  <Button size="sm" onClick={applyJson}>Apply JSON</Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setTab('content')}>Cancel</Button>
                </div>
              </div>
            )}
          </fieldset>

          <div className="d-flex gap-2 border-top pt-3 mt-3">
            <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save team page'}</Button>
            <Button size="sm" variant="outline-danger" onClick={doReset} disabled={saving}>
              {isPlatform ? 'Reset to defaults' : 'Use platform team'}
            </Button>
          </div>
        </>
      )}

      {editIdx !== null && form && (
        <MemberModal
          member={editIdx < 0 ? emptyMember() : form.members[editIdx]}
          isNew={editIdx < 0}
          functions={form.functions}
          expertiseAreas={form.expertiseAreas}
          memberNames={form.members.map((m) => m.name).filter(Boolean)}
          onClose={() => setEditIdx(null)}
          onSave={(m) => { upsertMember(editIdx, m); setEditIdx(null); }}
        />
      )}
    </div>
  );
}

// ---- Content tab ----
function ContentTab({ form, set, setListItem, addListItem, removeListItem, moveListItem, setExpertise }) {
  return (
    <>
      <Row className="g-2 mb-3">
        <Col md={6}>
          <Form.Label className="small mb-0">Organization name</Form.Label>
          <Form.Control size="sm" value={form.orgName} onChange={set('orgName')} />
        </Col>
        <Col md={6}>
          <Form.Label className="small mb-0">Intro line</Form.Label>
          <Form.Control size="sm" value={form.intro} onChange={set('intro')} />
        </Col>
      </Row>

      <h6>How we came together</h6>
      {form.story.map((para, idx) => (
        <div key={idx} className="d-flex gap-2 mb-2 align-items-start">
          <Form.Control
            size="sm" as="textarea" rows={2} value={para}
            onChange={(e) => setListItem('story', idx, e.target.value)}
          />
          <div className="d-flex flex-column gap-1">
            <Button size="sm" variant="outline-secondary" onClick={() => moveListItem('story', idx, -1)} title="Move up">↑</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => moveListItem('story', idx, 1)} title="Move down">↓</Button>
            <Button size="sm" variant="outline-danger" onClick={() => removeListItem('story', idx)} title="Remove">✕</Button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline-secondary" className="mb-3" onClick={() => addListItem('story', '')}>+ Add paragraph</Button>

      <h6>How we build and innovate</h6>
      {form.principles.map((p, idx) => (
        <Row key={idx} className="g-2 mb-2 border rounded p-2 mx-0">
          <Col md={4}><Form.Control size="sm" placeholder="Title" value={p.title || ''} onChange={(e) => setListItem('principles', idx, { title: e.target.value })} /></Col>
          <Col md={7}><Form.Control size="sm" as="textarea" rows={2} placeholder="Description" value={p.body || ''} onChange={(e) => setListItem('principles', idx, { body: e.target.value })} /></Col>
          <Col md={1}><Button size="sm" variant="outline-danger" onClick={() => removeListItem('principles', idx)}>✕</Button></Col>
        </Row>
      ))}
      <Button size="sm" variant="outline-secondary" className="mb-3" onClick={() => addListItem('principles', { title: '', body: '' })}>+ Add principle</Button>

      <h6>Functions / areas</h6>
      <p className="text-muted small mb-2">The org functions people belong to. A person can be in more than one (cross-functional).</p>
      {form.functions.map((fn, idx) => (
        <Row key={idx} className="g-2 mb-2 border rounded p-2 mx-0">
          <Col md={2}><Form.Control size="sm" placeholder="key" value={fn.key || ''} onChange={(e) => setListItem('functions', idx, { key: e.target.value })} /></Col>
          <Col md={3}><Form.Control size="sm" placeholder="Label" value={fn.label || ''} onChange={(e) => setListItem('functions', idx, { label: e.target.value })} /></Col>
          <Col md={6}><Form.Control size="sm" placeholder="Description" value={fn.description || ''} onChange={(e) => setListItem('functions', idx, { description: e.target.value })} /></Col>
          <Col md={1}><Button size="sm" variant="outline-danger" onClick={() => removeListItem('functions', idx)}>✕</Button></Col>
        </Row>
      ))}
      <Button size="sm" variant="outline-secondary" className="mb-3" onClick={() => addListItem('functions', { key: '', label: '', description: '' })}>+ Add function</Button>

      <h6>Areas of expertise</h6>
      <Form.Control
        size="sm" as="textarea" rows={3} className="mb-2"
        placeholder="One area per line — these become the tags you assign to people."
        value={form.expertiseAreas.join('\n')}
        onChange={(e) => setExpertise(e.target.value.split('\n').map((s) => s.replace(/^\s+/, '')))}
      />
    </>
  );
}

// ---- People tab: paginated table ----
function PeopleTab({ form, onAdd, onEdit, onRemove }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const funcLabel = (k) => form.functions.find((f) => f.key === k)?.label || k;

  const rows = useMemo(() => form.members
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => !q || `${m.name} ${m.title}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (a.m.order ?? 0) - (b.m.order ?? 0)),
  [form.members, q]);

  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const cur = Math.min(page, pages);
  const shown = rows.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center gap-2 mb-2 flex-wrap">
        <Button size="sm" onClick={onAdd}>+ Add position</Button>
        <Form.Control
          size="sm" style={{ maxWidth: 220 }} placeholder="Search name or title…"
          value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
        />
      </div>

      <div className="table-responsive">
        <Table hover size="sm" className="align-middle">
          <thead>
            <tr>
              <th style={{ width: 44 }} />
              <th>Name</th><th>Title / role</th><th>Level</th><th>Functions</th><th>Reports to</th>
              <th style={{ width: 60 }}>Order</th><th style={{ width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={8} className="text-muted text-center py-3">No people yet. Use "+ Add position".</td></tr>
            )}
            {shown.map(({ m, i }) => (
              <tr key={i}>
                <td>
                  {m.photoUrl
                    ? <img src={photoSrc(m.photoUrl)} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                    : <span className="d-inline-flex align-items-center justify-content-center bg-light text-muted" style={{ width: 32, height: 32, borderRadius: '50%', fontSize: 11 }}>
                        {(m.name || '?').slice(0, 1).toUpperCase()}
                      </span>}
                </td>
                <td>{m.name || <span className="text-muted">(unnamed)</span>}</td>
                <td className="small">{m.title}</td>
                <td><Badge bg={m.seniority === 'exec' ? 'primary' : m.seniority === 'lead' ? 'info' : 'secondary'}>{SENIORITY_LABEL[m.seniority]?.split(' ')[0] || m.seniority}</Badge></td>
                <td className="small">
                  {(m.functionKeys || []).length
                    ? m.functionKeys.map((k) => funcLabel(k)).join(', ')
                    : <span className="text-muted">—</span>}
                </td>
                <td className="small">{m.reportsTo || <span className="text-muted">—</span>}</td>
                <td className="small">{m.order}</td>
                <td className="text-nowrap">
                  <Button size="sm" variant="outline-secondary" onClick={() => onEdit(i)}>Edit</Button>{' '}
                  <Button size="sm" variant="outline-danger" onClick={() => onRemove(i)}>✕</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <span className="small text-muted">Page {cur} of {pages} · {rows.length} {rows.length === 1 ? 'person' : 'people'}</span>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" disabled={cur <= 1} onClick={() => setPage(cur - 1)}>‹ Prev</Button>
          <Button size="sm" variant="outline-secondary" disabled={cur >= pages} onClick={() => setPage(cur + 1)}>Next ›</Button>
        </div>
      </div>
    </>
  );
}

// ---- Hierarchy tab: adjust reporting lines + order ----
function HierarchyTab({ form, onAdd, onEdit, setManager, move }) {
  const byName = useMemo(() => new Map(form.members.map((m, i) => [m.name, i])), [form.members]);
  const childrenOf = (name) => form.members
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => (m.reportsTo || '') === (name || '') && m.name !== name)
    .sort((a, b) => (a.m.order ?? 0) - (b.m.order ?? 0));

  // roots = members with no manager, or a manager that isn't in the list
  const roots = form.members
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => !m.reportsTo || !byName.has(m.reportsTo))
    .sort((a, b) => (a.m.order ?? 0) - (b.m.order ?? 0));

  const managerOptions = form.members.map((m) => m.name).filter(Boolean);

  const seen = new Set();
  const renderNode = ({ m, i }, depth) => {
    if (seen.has(i)) return null; // guard against a cycle
    seen.add(i);
    const kids = childrenOf(m.name);
    return (
      <div key={i} style={{ marginLeft: depth * 20 }} className="py-1 border-bottom">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {depth > 0 && <span className="text-muted">└</span>}
          <strong className="small">{m.name || '(unnamed)'}</strong>
          <span className="text-muted small">{m.title}</span>
          <Badge bg={m.seniority === 'exec' ? 'primary' : m.seniority === 'lead' ? 'info' : 'secondary'} className="ms-1">
            {(SENIORITY_LABEL[m.seniority] || m.seniority).split(' ')[0]}
          </Badge>
          <div className="ms-auto d-flex align-items-center gap-1">
            <Form.Select
              size="sm" style={{ width: 190 }} value={m.reportsTo || ''}
              onChange={(e) => setManager(i, e.target.value)}
              title="Reports to"
            >
              <option value="">— top of chart —</option>
              {managerOptions.filter((n) => n !== m.name).map((n) => <option key={n} value={n}>{n}</option>)}
            </Form.Select>
            <Button size="sm" variant="outline-secondary" onClick={() => move(i, -1)} title="Move up among peers">↑</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => move(i, 1)} title="Move down among peers">↓</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => onEdit(i)}>Edit</Button>
          </div>
        </div>
        {kids.map((k) => renderNode(k, depth + 1))}
      </div>
    );
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <p className="text-muted small mb-0">
          Drag-free hierarchy editor: set each person's manager and reorder peers. Changes apply
          when you Save.
        </p>
        <Button size="sm" onClick={onAdd}>+ Add position</Button>
      </div>
      {roots.length === 0 && <Alert variant="light" className="small">No people yet.</Alert>}
      {roots.map((r) => renderNode(r, 0))}
      {/* anyone left unrendered (part of a cycle) */}
      {form.members.map((m, i) => (seen.has(i) ? null : (
        <div key={`orphan-${i}`} className="py-1 text-danger small">
          {m.name || '(unnamed)'} — reporting loop, fix "Reports to"
        </div>
      )))}
    </>
  );
}

// ---- Member add/edit modal ----
function MemberModal({ member, isNew, functions, expertiseAreas, memberNames, onClose, onSave }) {
  const [m, setM] = useState({ ...emptyMember(), ...member });
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const upd = (patch) => setM((x) => ({ ...x, ...patch }));
  const toggle = (key, value) => setM((x) => {
    const has = (x[key] || []).includes(value);
    return { ...x, [key]: has ? x[key].filter((v) => v !== value) : [...(x[key] || []), value] };
  });

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true); setErr('');
    try {
      const [url] = await uploadImages([file]);
      upd({ photoUrl: url });
    } catch (e) {
      setErr(e.response?.data?.msg || 'Unable to upload that photo.');
    } finally { setUploading(false); }
  };

  const extraExpertise = (m.expertise || []).filter((e) => !expertiseAreas.includes(e));

  return (
    <Modal show onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{isNew ? 'Add position' : `Edit — ${m.name || 'position'}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger" className="py-2">{err}</Alert>}
        <Row className="g-2">
          <Col xs={12} md={3} className="text-center">
            {m.photoUrl
              ? <img src={photoSrc(m.photoUrl)} alt={m.name} style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: '50%' }} />
              : <div className="d-flex align-items-center justify-content-center bg-light text-muted mx-auto" style={{ width: 84, height: 84, borderRadius: '50%' }}>Photo</div>}
            <Form.Control size="sm" type="file" accept="image/*" className="mt-1" disabled={uploading}
              onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            {uploading && <Spinner animation="border" size="sm" className="mt-1" />}
            {m.photoUrl && (
              <Button size="sm" variant="link" className="p-0" onClick={() => upd({ photoUrl: '' })}>remove photo</Button>
            )}
          </Col>
          <Col xs={12} md={9}>
            <Row className="g-2">
              <Col md={7}>
                <Form.Label className="small mb-0">Name</Form.Label>
                <Form.Control size="sm" value={m.name} onChange={(e) => upd({ name: e.target.value })} placeholder="e.g. Position Open" />
              </Col>
              <Col md={5}>
                <Form.Label className="small mb-0">Display order</Form.Label>
                <Form.Control size="sm" type="number" value={m.order} onChange={(e) => upd({ order: e.target.value })} />
              </Col>
              <Col md={12}>
                <Form.Label className="small mb-0">Title / role</Form.Label>
                <Form.Control size="sm" value={m.title} onChange={(e) => upd({ title: e.target.value })} placeholder="e.g. Head of Finance" />
              </Col>
              <Col md={6}>
                <Form.Label className="small mb-0">Level in the chart</Form.Label>
                <Form.Select size="sm" value={m.seniority} onChange={(e) => upd({ seniority: e.target.value })}>
                  {SENIORITY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label className="small mb-0">Reports to</Form.Label>
                <Form.Select size="sm" value={m.reportsTo} onChange={(e) => upd({ reportsTo: e.target.value })}>
                  <option value="">— top of chart —</option>
                  {memberNames.filter((n) => n !== m.name).map((n) => <option key={n} value={n}>{n}</option>)}
                </Form.Select>
              </Col>
              <Col xs={12}>
                <Form.Label className="small mb-0">Short bio</Form.Label>
                <Form.Control size="sm" as="textarea" rows={2} value={m.bio} onChange={(e) => upd({ bio: e.target.value })} />
              </Col>
            </Row>
          </Col>
        </Row>

        <div className="mt-3">
          <div className="small fw-semibold">Functions <span className="text-muted">— pick any (cross-functional)</span></div>
          <div className="d-flex flex-wrap gap-3">
            {functions.filter((f) => f.key).map((f) => (
              <Form.Check
                key={f.key} type="checkbox" id={`mm-fn-${f.key}`} label={f.label || f.key}
                checked={(m.functionKeys || []).includes(f.key)}
                onChange={() => toggle('functionKeys', f.key)}
              />
            ))}
            {functions.filter((f) => f.key).length === 0 && <span className="small text-muted">Add functions on the Content tab first.</span>}
          </div>
        </div>

        <div className="mt-3">
          <div className="small fw-semibold">Areas of expertise</div>
          <div className="d-flex flex-wrap gap-3">
            {expertiseAreas.filter(Boolean).map((a) => (
              <Form.Check
                key={a} type="checkbox" id={`mm-exp-${a}`} label={a}
                checked={(m.expertise || []).includes(a)}
                onChange={() => toggle('expertise', a)}
              />
            ))}
            {expertiseAreas.filter(Boolean).length === 0 && <span className="small text-muted">Add areas of expertise on the Content tab first.</span>}
          </div>
          {extraExpertise.length > 0 && (
            <div className="small text-muted mt-1">Also tagged: {extraExpertise.join(', ')}</div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={() => onSave({ ...m, order: Number(m.order) || 0 })}>
          {isNew ? 'Add position' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
